import type { Edge, Node } from 'reactflow';
import type { OrgChartPerson, OrgDepartment, OrgLevel } from '../types/purchasing';
import { ORG_DEPARTMENTS, ORG_LEVELS } from '../types/purchasing';
import {
  chartManagerId,
  isOrgChartBot,
  ORG_CHART_LANE_LABELS,
  ORG_CHART_LANES,
  ORG_DEPARTMENT_LABELS,
  orgSeatOf,
  resolveOrgPlacement,
  type OrgChartLane,
} from './orgChart';
import { applyEffectiveReporting, canManage } from './orgReporting';

export const ORG_LAYOUT = {
  laneLabelWidth: 132,
  colHeaderHeight: 52,
  cellWidth: 320,
  cellPaddingX: 28,
  cellPaddingY: 32,
  cardWidth: 176,
  cardHeight: 132,
  cardGapX: 16,
  cardGapY: 16,
  minLaneHeight: 220,
  /** Gap between the Shop column and the 1099 pool panel. */
  poolGapX: 36,
  /** Space between the Staff lane and the Bot Caste container. */
  botCasteGap: 40,
  poolWidth: 320,
  poolPaddingX: 16,
  poolPaddingY: 16,
  poolHeaderHeight: 48,
  poolCardWidth: 136,
  poolCardHeight: 104,
  poolCardGap: 12,
};

export const ORG_POOL_NODE_ID = 'pool-contractors';
export const ORG_POOL_EDGE_ID = 'report-pool-contractors';
export const ORG_BOT_CASTE_NODE_ID = 'bot-caste';
export const ORG_BOT_CASTE_EDGE_ID = 'report-bot-caste';

export const ORG_GROUP_COLORS: Record<OrgDepartment, string> = {
  sales: '#b45309',
  office: '#2563eb',
  shop: '#15803d',
};

export const ORG_POOL_COLOR = '#8a6a3d';
export const ORG_BOT_CASTE_COLOR = '#64748b';

export type OrgPersonNodeData = {
  person: OrgChartPerson;
  /** Compact card inside the 1099 pool. */
  compact?: boolean;
};
export type OrgLaneNodeData = { level: OrgChartLane; label: string };
export type OrgColLabelNodeData = { department: OrgDepartment; label: string };
export type OrgPoolNodeData = { label: string; count: number };

export type OrgChartFlowNode =
  | Node<OrgColLabelNodeData, 'orgColLabel'>
  | Node<OrgLaneNodeData, 'orgLane'>
  | Node<OrgPersonNodeData, 'orgPerson'>
  | Node<OrgPoolNodeData, 'orgPool'>;

export type OrgChartCellRect = {
  level: OrgChartLane;
  department: OrgDepartment;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Staff may be dropped here (Managerial / Staff only). */
  droppable: boolean;
};

export type OrgChartPoolRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  headerHeight: number;
  label: string;
  count: number;
};

function isContractor(person: OrgChartPerson): boolean {
  return orgSeatOf(person) === 'contractor' && !isOrgChartBot(person);
}

function isApex(person: OrgChartPerson): boolean {
  return orgSeatOf(person) === 'ceo';
}

/** Everyone who sits in the lane/column grid (not the pool). */
function gridPeople(people: OrgChartPerson[]): OrgChartPerson[] {
  return people.filter(p => !isContractor(p));
}

function poolPeople(people: OrgChartPerson[]): OrgChartPerson[] {
  return people.filter(isContractor).slice().sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function chartLanes(people: OrgChartPerson[]): OrgChartLane[] {
  return gridPeople(people).some(isOrgChartBot) ? [...ORG_CHART_LANES] : [...ORG_LEVELS];
}

function cellMembers(
  people: OrgChartPerson[],
  level: OrgChartLane,
  department: OrgDepartment,
): OrgChartPerson[] {
  return gridPeople(people).filter(p => {
    if (isApex(p)) return false;
    const bot = isOrgChartBot(p);
    if (level === 'bots') return bot && resolveOrgPlacement(p).orgDepartment === department;
    if (bot) return false;
    const place = resolveOrgPlacement(p);
    return place.orgLevel === level && place.orgDepartment === department;
  });
}

function reportingDepthInCell(person: OrgChartPerson, cell: OrgChartPerson[]): number {
  const byId = new Map(cell.map(p => [p.uid, p]));
  let depth = 0;
  const seen = new Set<string>();
  let cur = chartManagerId(person);
  while (cur && byId.has(cur) && !seen.has(cur)) {
    seen.add(cur);
    depth += 1;
    cur = chartManagerId(byId.get(cur) as OrgChartPerson);
  }
  return depth;
}

function seatRank(person: OrgChartPerson): number {
  switch (orgSeatOf(person)) {
    case 'ceo': return 0;
    case 'coo': return 1;
    case 'cfo': return 1;
    case 'office_manager': return 1;
    default: return 2;
  }
}

export function sortCellByReporting(cell: OrgChartPerson[]): OrgChartPerson[] {
  return cell.slice().sort((a, b) => {
    const ra = seatRank(a);
    const rb = seatRank(b);
    if (ra !== rb) return ra - rb;
    const da = reportingDepthInCell(a, cell);
    const db = reportingDepthInCell(b, cell);
    if (da !== db) return da - db;
    return a.displayName.localeCompare(b.displayName);
  });
}

function cardsPerRow(cell: OrgChartPerson[]): number {
  if (cell.length <= 1) return 1;
  const ids = new Set(cell.map(p => p.uid));
  if (cell.some(p => seatRank(p) < 2)) return 1;
  if (cell.some(p => {
    const mgr = chartManagerId(p);
    return mgr && ids.has(mgr);
  })) {
    return 1;
  }
  return 2;
}

function peopleInCell(
  people: OrgChartPerson[],
  level: OrgChartLane,
  department: OrgDepartment,
): OrgChartPerson[] {
  return sortCellByReporting(cellMembers(people, level, department));
}

function apexPeople(people: OrgChartPerson[]): OrgChartPerson[] {
  return gridPeople(people)
    .filter(p => isApex(p) && !isOrgChartBot(p))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/** CEO sits on row 0 of Executive, centered across the whole grid. */
function reservedApexRows(people: OrgChartPerson[], level: OrgChartLane): number {
  if (level !== 'executive') return 0;
  return apexPeople(people).length > 0 ? 1 : 0;
}

function rowsInCell(cell: OrgChartPerson[]): number {
  if (cell.length === 0) return 0;
  return Math.ceil(cell.length / cardsPerRow(cell));
}

function rowsForLevel(people: OrgChartPerson[], level: OrgChartLane): number {
  const apexRows = reservedApexRows(people, level);
  const restRows = Math.max(0, ...ORG_DEPARTMENTS.map(dept => rowsInCell(peopleInCell(people, level, dept))));
  return Math.max(1, apexRows + restRows);
}

function botPeople(people: OrgChartPerson[]): OrgChartPerson[] {
  return gridPeople(people)
    .filter(isOrgChartBot)
    .slice()
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function botCardsPerRow(): number {
  const inner = gridWidth() - ORG_LAYOUT.laneLabelWidth - ORG_LAYOUT.cellPaddingX * 2;
  return Math.max(
    1,
    Math.floor((inner + ORG_LAYOUT.cardGapX) / (ORG_LAYOUT.cardWidth + ORG_LAYOUT.cardGapX)),
  );
}

function botLaneHeight(people: OrgChartPerson[]): number {
  const count = botPeople(people).length;
  const rows = Math.max(1, Math.ceil(count / botCardsPerRow()));
  const stacked = ORG_LAYOUT.cellPaddingY * 2
    + rows * ORG_LAYOUT.cardHeight
    + Math.max(0, rows - 1) * ORG_LAYOUT.cardGapY;
  return Math.max(ORG_LAYOUT.minLaneHeight, stacked);
}

function laneHeightFor(people: OrgChartPerson[], level: OrgChartLane): number {
  if (level === 'bots') return botLaneHeight(people);
  const rows = rowsForLevel(people, level);
  const stacked = ORG_LAYOUT.cellPaddingY * 2
    + rows * ORG_LAYOUT.cardHeight
    + Math.max(0, rows - 1) * ORG_LAYOUT.cardGapY;
  return Math.max(ORG_LAYOUT.minLaneHeight, stacked);
}

function gridWidth(): number {
  return ORG_LAYOUT.laneLabelWidth + ORG_DEPARTMENTS.length * ORG_LAYOUT.cellWidth;
}

function poolColumns(): number {
  const inner = ORG_LAYOUT.poolWidth - ORG_LAYOUT.poolPaddingX * 2;
  return Math.max(1, Math.floor((inner + ORG_LAYOUT.poolCardGap) / (ORG_LAYOUT.poolCardWidth + ORG_LAYOUT.poolCardGap)));
}

function poolNeededHeight(count: number): number {
  const rows = Math.ceil(count / poolColumns());
  return ORG_LAYOUT.poolHeaderHeight
    + ORG_LAYOUT.poolPaddingY * 2
    + Math.max(rows, 1) * ORG_LAYOUT.poolCardHeight
    + Math.max(0, rows - 1) * ORG_LAYOUT.poolCardGap;
}

export function orgChartCanvasSize(people: OrgChartPerson[]): { width: number; height: number } {
  const lanes = chartLanes(people);
  const laneHeights = lanes.map(level => laneHeightFor(people, level));
  const botGap = lanes.includes('bots') ? ORG_LAYOUT.botCasteGap : 0;
  const gridHeight = ORG_LAYOUT.colHeaderHeight + laneHeights.reduce((sum, h) => sum + h, 0) + botGap;
  const pool = poolPeople(people);
  const execH = laneHeightFor(people, 'executive');
  const poolTop = ORG_LAYOUT.colHeaderHeight + execH;
  const staffBottom = poolTop + laneHeightFor(people, 'managerial') + laneHeightFor(people, 'general');
  const height = pool.length > 0
    ? Math.max(gridHeight, staffBottom, poolTop + poolNeededHeight(pool.length))
    : gridHeight;
  return {
    width: gridWidth() + ORG_LAYOUT.poolGapX + ORG_LAYOUT.poolWidth,
    height,
  };
}

export const ORG_EDGE_TYPE = 'orgBezier';

function edgeFor(source: string, target: string, group: OrgDepartment, pinned: boolean): Edge {
  return {
    id: `report-${target}`,
    source,
    target,
    type: ORG_EDGE_TYPE,
    style: { stroke: ORG_GROUP_COLORS[group], strokeWidth: pinned ? 2.2 : 1.5 },
  };
}

export function buildOrgChartLayout(input: OrgChartPerson[]): {
  nodes: Node[];
  edges: Edge[];
  cells: OrgChartCellRect[];
  pool: OrgChartPoolRect | null;
  width: number;
  height: number;
} {
  const people = input.some(p => p.effectiveReportsToUserId === undefined)
    ? applyEffectiveReporting(input)
    : input;
  const { width, height } = orgChartCanvasSize(people);
  const nodes: Node[] = [];
  const cells: OrgChartCellRect[] = [];
  const headerTop = 0;
  const grid = gridWidth();

  const lanes = chartLanes(people);
  const laneTops: Record<OrgChartLane, number> = {
    executive: ORG_LAYOUT.colHeaderHeight,
    managerial: 0,
    general: 0,
    bots: 0,
  };
  let top = ORG_LAYOUT.colHeaderHeight;
  for (const level of lanes) {
    if (level === 'bots') top += ORG_LAYOUT.botCasteGap;
    const laneHeight = laneHeightFor(people, level);
    laneTops[level] = top;
    nodes.push({
      id: `lane-${level}`,
      type: 'orgLane',
      position: { x: 0, y: top },
      data: { level, label: ORG_CHART_LANE_LABELS[level] },
      draggable: false,
      selectable: false,
      zIndex: 0,
      style: { width: grid, height: laneHeight },
    });
    top += laneHeight;
  }

  ORG_DEPARTMENTS.forEach((department, col) => {
    nodes.push({
      id: `col-${department}`,
      type: 'orgColLabel',
      position: {
        x: ORG_LAYOUT.laneLabelWidth + col * ORG_LAYOUT.cellWidth,
        y: headerTop,
      },
      data: { department, label: ORG_DEPARTMENT_LABELS[department] },
      draggable: false,
      selectable: false,
      zIndex: 1,
      style: { width: ORG_LAYOUT.cellWidth, height: ORG_LAYOUT.colHeaderHeight },
    });
  });

  const pushPerson = (
    person: OrgChartPerson,
    x: number,
    y: number,
    size: { width: number; height: number },
    compact = false,
  ) => {
    nodes.push({
      id: person.uid,
      type: 'orgPerson',
      position: { x, y },
      data: { person, compact },
      draggable: false,
      selectable: true,
      zIndex: 2,
      width: size.width,
      height: size.height,
    });
  };

  // CEO apex strip — centered across the grid on the first Executive row.
  const apex = apexPeople(people);
  if (apex.length > 0) {
    const stripW = apex.length * ORG_LAYOUT.cardWidth + Math.max(0, apex.length - 1) * ORG_LAYOUT.cardGapX;
    const originX = ORG_LAYOUT.laneLabelWidth + (grid - ORG_LAYOUT.laneLabelWidth - stripW) / 2;
    const y = laneTops.executive + ORG_LAYOUT.cellPaddingY;
    apex.forEach((person, index) => {
      pushPerson(
        person,
        originX + index * (ORG_LAYOUT.cardWidth + ORG_LAYOUT.cardGapX),
        y,
        { width: ORG_LAYOUT.cardWidth, height: ORG_LAYOUT.cardHeight },
      );
    });
  }

  for (const level of lanes) {
    const laneHeight = laneHeightFor(people, level);
    const apexRows = reservedApexRows(people, level);
    if (level === 'bots') {
      ORG_DEPARTMENTS.forEach((department, col) => {
        cells.push({
          level,
          department,
          x: ORG_LAYOUT.laneLabelWidth + col * ORG_LAYOUT.cellWidth,
          y: laneTops[level],
          width: ORG_LAYOUT.cellWidth,
          height: laneHeight,
          droppable: false,
        });
      });
      continue;
    }
    ORG_DEPARTMENTS.forEach((department, col) => {
      cells.push({
        level,
        department,
        x: ORG_LAYOUT.laneLabelWidth + col * ORG_LAYOUT.cellWidth,
        y: laneTops[level],
        width: ORG_LAYOUT.cellWidth,
        height: laneHeight,
        droppable: level !== 'executive',
      });
      const cell = peopleInCell(people, level, department);
      const cols = cardsPerRow(cell);
      const rows = rowsInCell(cell);
      const packRows = Math.max(apexRows + rows, 1);
      const packH = packRows * ORG_LAYOUT.cardHeight + Math.max(0, packRows - 1) * ORG_LAYOUT.cardGapY;
      const packTop = apexRows > 0
        ? laneTops[level] + ORG_LAYOUT.cellPaddingY
        : laneTops[level] + (laneHeight - packH) / 2;
      const gridW = Math.max(cols, 1) * ORG_LAYOUT.cardWidth + Math.max(0, cols - 1) * ORG_LAYOUT.cardGapX;
      const originX = ORG_LAYOUT.laneLabelWidth + col * ORG_LAYOUT.cellWidth + (ORG_LAYOUT.cellWidth - gridW) / 2;
      cell.forEach((person, index) => {
        const rowIndex = apexRows + Math.floor(index / cols);
        const colIndex = index % cols;
        pushPerson(
          person,
          originX + colIndex * (ORG_LAYOUT.cardWidth + ORG_LAYOUT.cardGapX),
          packTop + rowIndex * (ORG_LAYOUT.cardHeight + ORG_LAYOUT.cardGapY),
          { width: ORG_LAYOUT.cardWidth, height: ORG_LAYOUT.cardHeight },
        );
      });
    });
  }

  const bots = botPeople(people);
  if (bots.length > 0 && lanes.includes('bots')) {
    const cols = Math.min(botCardsPerRow(), bots.length);
    const gridW = cols * ORG_LAYOUT.cardWidth + Math.max(0, cols - 1) * ORG_LAYOUT.cardGapX;
    const innerLeft = ORG_LAYOUT.laneLabelWidth;
    const innerWidth = grid - innerLeft;
    const originX = innerLeft + (innerWidth - gridW) / 2;
    const originY = laneTops.bots + ORG_LAYOUT.cellPaddingY;
    bots.forEach((person, index) => {
      const rowIndex = Math.floor(index / cols);
      const colIndex = index % cols;
      pushPerson(
        person,
        originX + colIndex * (ORG_LAYOUT.cardWidth + ORG_LAYOUT.cardGapX),
        originY + rowIndex * (ORG_LAYOUT.cardHeight + ORG_LAYOUT.cardGapY),
        { width: ORG_LAYOUT.cardWidth, height: ORG_LAYOUT.cardHeight },
      );
    });
    const aisleX = ORG_LAYOUT.laneLabelWidth + 2 * ORG_LAYOUT.cellWidth;
    nodes.push({
      id: ORG_BOT_CASTE_NODE_ID,
      type: 'orgBotCaste',
      position: { x: aisleX - 1, y: laneTops.bots },
      data: { label: ORG_CHART_LANE_LABELS.bots, count: bots.length },
      draggable: false,
      selectable: false,
      zIndex: 1,
      width: 2,
      height: 2,
    });
  }

  // 1099 pool — right of Shop, spanning Managerial + Staff.
  const contractors = poolPeople(people);
  let pool: OrgChartPoolRect | null = null;
  if (contractors.length > 0) {
    const poolX = grid + ORG_LAYOUT.poolGapX;
    const poolTop = laneTops.managerial;
    const staffBottom = laneTops.general + laneHeightFor(people, 'general');
    const poolH = Math.max(staffBottom - poolTop, poolNeededHeight(contractors.length));
    pool = {
      x: poolX,
      y: poolTop,
      width: ORG_LAYOUT.poolWidth,
      height: poolH,
      headerHeight: ORG_LAYOUT.poolHeaderHeight,
      label: '1099 contractor pool',
      count: contractors.length,
    };
    nodes.push({
      id: ORG_POOL_NODE_ID,
      type: 'orgPool',
      position: { x: poolX, y: poolTop },
      data: { label: pool.label, count: contractors.length },
      draggable: false,
      selectable: false,
      zIndex: 1,
      width: ORG_LAYOUT.poolWidth,
      height: ORG_LAYOUT.poolHeaderHeight,
      style: { width: ORG_LAYOUT.poolWidth, height: ORG_LAYOUT.poolHeaderHeight },
    });
    const cols = poolColumns();
    const innerW = cols * ORG_LAYOUT.poolCardWidth + (cols - 1) * ORG_LAYOUT.poolCardGap;
    const originX = poolX + (ORG_LAYOUT.poolWidth - innerW) / 2;
    const originY = poolTop + ORG_LAYOUT.poolHeaderHeight + ORG_LAYOUT.poolPaddingY;
    contractors.forEach((person, index) => {
      pushPerson(
        person,
        originX + (index % cols) * (ORG_LAYOUT.poolCardWidth + ORG_LAYOUT.poolCardGap),
        originY + Math.floor(index / cols) * (ORG_LAYOUT.poolCardHeight + ORG_LAYOUT.poolCardGap),
        { width: ORG_LAYOUT.poolCardWidth, height: ORG_LAYOUT.poolCardHeight },
        true,
      );
    });
  }

  const ids = new Set(people.map(p => p.uid));
  const edges: Edge[] = [];
  for (const p of gridPeople(people)) {
    if (isOrgChartBot(p)) continue;
    const mgr = chartManagerId(p);
    if (!mgr || !ids.has(mgr)) continue;
    const place = resolveOrgPlacement(p);
    edges.push(edgeFor(mgr, p.uid, place.orgDepartment, p.pinned === true || orgSeatOf(p) !== 'staff'));
  }
  if (bots.length > 0) {
    const ceo = people.find(p => isApex(p) && !isOrgChartBot(p));
    if (ceo && ids.has(ceo.uid)) {
      edges.push({
        id: ORG_BOT_CASTE_EDGE_ID,
        source: ceo.uid,
        target: ORG_BOT_CASTE_NODE_ID,
        type: ORG_EDGE_TYPE,
        style: { stroke: ORG_BOT_CASTE_COLOR, strokeWidth: 2.4 },
        zIndex: 4,
      });
    }
  }
  if (pool) {
    const head = contractors.map(chartManagerId).find(id => id && ids.has(id));
    if (head) {
      edges.push({
        id: ORG_POOL_EDGE_ID,
        source: head,
        target: ORG_POOL_NODE_ID,
        type: ORG_EDGE_TYPE,
        style: { stroke: ORG_POOL_COLOR, strokeWidth: 2.4, strokeDasharray: '6 4' },
      });
    }
  }

  return { nodes, edges, cells, pool, width, height };
}

export type OrgDropHit =
  | { type: 'person'; person: OrgChartPerson }
  | { type: 'cell'; level: OrgLevel; department: OrgDepartment };

function contains(rect: { x: number; y: number; width: number; height: number }, point: { x: number; y: number }): boolean {
  return point.x >= rect.x
    && point.x <= rect.x + rect.width
    && point.y >= rect.y
    && point.y <= rect.y + rect.height;
}

/**
 * Prefer a person card under the pointer (only when that person may manage
 * the dragged staffer), then a droppable Managerial / Staff cell. The
 * Executive lane and the 1099 pool are never drop targets.
 */
export function hitTestOrgDrop(
  point: { x: number; y: number },
  draggingUid: string,
  people: Array<{ uid: string; x: number; y: number; person: OrgChartPerson }>,
  cells: OrgChartCellRect[],
): OrgDropHit | null {
  const roster = people.map(p => p.person);
  const dragging = roster.find(p => p.uid === draggingUid);
  for (const p of people) {
    if (p.uid === draggingUid) continue;
    if (orgSeatOf(p.person) === 'contractor') continue;
    if (isOrgChartBot(p.person)) continue;
    if (!contains({ x: p.x, y: p.y, width: ORG_LAYOUT.cardWidth, height: ORG_LAYOUT.cardHeight }, point)) continue;
    if (dragging && !canManage(p.person, dragging, roster)) return null;
    return { type: 'person', person: p.person };
  }
  for (const cell of cells) {
    if (!cell.droppable) continue;
    if (cell.level === 'executive' || cell.level === 'bots') continue;
    if (contains(cell, point)) return { type: 'cell', level: cell.level, department: cell.department };
  }
  return null;
}
